import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";

interface MegaItem {
  label: string;
  description: string;
  icon: string;
  to: string;
}

interface MegaGroup {
  title: string;
  items: MegaItem[];
}

const MEGA_GROUPS: MegaGroup[] = [
  {
    title: "Infraestrutura e painéis",
    items: [
      { label: "Hospedagem de Sites", description: "Planos, recursos e desempenho.", icon: "ri-server-line", to: "/blog/hospedagem-de-sites" },
      { label: "VPS e Servidores", description: "Servidores virtuais e Linux.", icon: "ri-hard-drive-3-line", to: "/blog/vps-servidores" },
      { label: "Domínios e DNS", description: "Registro, DNS e apontamento.", icon: "ri-global-line", to: "/blog/dominios" },
      { label: "Banco de Dados", description: "MySQL, MariaDB e phpMyAdmin.", icon: "ri-database-2-line", to: "/blog/banco-de-dados" },
      { label: "SSH e Linux", description: "Terminal e comandos Linux.", icon: "ri-terminal-box-line", to: "/blog/ssh-linux" },
    ],
  },
  {
    title: "Painéis e tutoriais",
    items: [
      { label: "DirectAdmin", description: "E-mails, bancos, SSL e backup.", icon: "ri-window-2-line", to: "/blog/directadmin" },
      { label: "cPanel", description: "Tutoriais completos de cPanel.", icon: "ri-dashboard-3-line", to: "/blog/cpanel" },
      { label: "MySQL", description: "Criar banco, usuário e backup.", icon: "ri-archive-stack-line", to: "/blog/mysql" },
      { label: "Guias e Tutoriais", description: "Passo a passo por nível.", icon: "ri-guide-line", to: "/blog/guias-tutoriais" },
    ],
  },
  {
    title: "Criar, vender e crescer",
    items: [
      { label: "Criação de Sites", description: "Sites profissionais e SEO.", icon: "ri-layout-masonry-line", to: "/blog/criacao-de-sites" },
      { label: "Loja Virtual", description: "Como vender pela internet.", icon: "ri-shopping-bag-3-line", to: "/blog/loja-virtual" },
      { label: "SEO", description: "Aparecer no Google.", icon: "ri-search-eye-line", to: "/blog/seo" },
      { label: "Google Ads", description: "Anunciar e medir resultados.", icon: "ri-google-fill", to: "/blog/google-ads" },
      { label: "Marketing Digital", description: "Estratégia e conversão.", icon: "ri-megaphone-line", to: "/blog/marketing-digital" },
    ],
  },
];

export default function BlogMegaMenu({ solid }: { solid: boolean }) {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    const onPointerDown = (event: globalThis.MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, []);

  const isActive = location.pathname.startsWith("/blog");

  return (
    <div ref={ref} className="relative" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-haspopup="true"
        className={`inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
          solid
            ? isActive
              ? "bg-background-100 text-primary-600"
              : "text-foreground-700 hover:bg-background-100 hover:text-primary-600"
            : "text-background-100 hover:bg-background-50/10 hover:text-background-50"
        }`}
      >
        Blog
        <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${open ? "rotate-180" : ""}`} aria-hidden="true"></i>
      </button>

      <div
        className={`absolute left-0 top-full z-50 mt-3 w-[46rem] max-w-[calc(100vw-2rem)] origin-top-left rounded-xl border border-background-200 bg-background-50 p-6 transition-all duration-200 ${
          open ? "visible scale-100 opacity-100" : "invisible -translate-y-1 scale-[0.98] opacity-0"
        }`}
        role="menu"
        aria-label="Central de conhecimento"
      >
        <div className="grid grid-cols-1 gap-x-6 gap-y-7 sm:grid-cols-3">
          {MEGA_GROUPS.map((group) => (
            <div key={group.title}>
              <p className="text-[11px] font-semibold uppercase tracking-wider text-foreground-400">{group.title}</p>
              <ul className="mt-3 flex flex-col gap-1">
                {group.items.map((item) => (
                  <li key={item.label}>
                    <Link
                      to={item.to}
                      onClick={() => setOpen(false)}
                      className="group flex items-start gap-3 rounded-lg p-2 transition-colors hover:bg-background-100"
                    >
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary-50 text-primary-600 transition-colors group-hover:bg-primary-500 group-hover:text-background-50">
                        <i className={`${item.icon} text-lg`} aria-hidden="true"></i>
                      </span>
                      <span className="min-w-0">
                        <span className="block text-sm font-semibold text-foreground-950">{item.label}</span>
                        <span className="mt-0.5 block text-xs leading-snug text-foreground-500">{item.description}</span>
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-6 flex flex-wrap items-center justify-between gap-3 border-t border-background-200 pt-5">
          <p className="text-xs text-foreground-500">Central de conhecimento NXHOST sobre tecnologia e negócios digitais.</p>
          <Link
            to="/blog"
            onClick={() => setOpen(false)}
            className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-background-50 transition-colors hover:bg-primary-600"
          >
            Ir para o Blog
            <i className="ri-arrow-right-line" aria-hidden="true"></i>
          </Link>
        </div>
      </div>
    </div>
  );
}